import { InfoIcon } from 'lucide-react';

import { Tooltip } from '@/components/Tooltip';
import { Container } from '@/components/legacy-ui';
import { cn } from '@/utils/classname';

export interface UsageDisplayProps {
  label: string;
  current: number;
  limit: number;
  showTooltip?: boolean;
  tooltipText?: string;
}

export function UsageDisplay({
  label,
  current,
  limit,
  showTooltip = false,
  tooltipText,
}: UsageDisplayProps) {
  const percentage = limit > 0 ? (current / limit) * 100 : 0;
  const isOverLimit = limit > 0 && current > limit;
  const isApproachingLimit = limit > 0 && !isOverLimit && percentage >= 90;

  // Color states: normal (white), warning (yellow), over (red)
  const textColor = isOverLimit
    ? 'text-red-500'
    : isApproachingLimit
      ? 'text-amber-400'
      : 'text-chariot-text-primary';
  const barColor = isOverLimit
    ? 'bg-red-500'
    : isApproachingLimit
      ? 'bg-amber-400'
      : 'bg-chariot-text-secondary';

  return (
    <Container className="flex-1">
      <div className="mb-2 flex items-center gap-2">
        <div className="text-chariot-text-primary flex items-center gap-2 text-base font-medium">
          {label}
          {showTooltip && tooltipText && (
            <Tooltip title={tooltipText}>
              <InfoIcon className="text-chariot-text-secondary size-4" />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="mb-3 flex items-baseline gap-1">
        <span className={cn('text-2xl font-bold', textColor)}>
          {current.toLocaleString()}
        </span>
        {limit > 0 && (
          <>
            <span className="text-chariot-text-primary text-lg">/</span>
            <span className="text-chariot-text-primary text-lg">
              {limit.toLocaleString()}
            </span>
          </>
        )}
      </div>

      {limit > 0 && (
        <>
          <div className="bg-chariot-background-secondary h-1 w-full overflow-hidden rounded-full">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-300',
                barColor
              )}
              style={{
                width: `${Math.min(percentage, 100)}%`,
              }}
            />
          </div>
          {isApproachingLimit && (
            <p className="mt-1 text-xs text-amber-400">Approaching limit</p>
          )}
          {isOverLimit && (
            <p className="mt-1 text-xs text-red-500">Over contract limit</p>
          )}
        </>
      )}
    </Container>
  );
}
